"use client";

import { useEffect } from "react";

export type RouteErrorContext = {
  boundary?: string;
  app?: string;
};

/** Logs route-boundary errors with digest/name only — never error.message (may contain PII). */
export function logRouteError(
  error: Error & { digest?: string },
  context?: RouteErrorContext,
): void {
  const payload = {
    boundary: context?.boundary,
    app: context?.app,
    digest: error.digest,
    name: error.name,
  };

  console.error("[route-error]", payload);

  const rumLabel = error.digest
    ? `[route-error] ${error.name} (${error.digest})`
    : `[route-error] ${error.name}`;

  void import("@datadog/browser-rum")
    .then(({ datadogRum }) => {
      if (!datadogRum.getInitConfiguration()) {
        return;
      }
      datadogRum.addError(new Error(rumLabel), {
        digest: error.digest,
        boundary: context?.boundary,
        app: context?.app,
        name: error.name,
      });
    })
    .catch(() => {
      // RUM unavailable (SSR, consent denied, or init pending).
    });
}

/**
 * Logs a client-side failure to Datadog RUM as an error with a synthetic label
 * and safe context only — never exception messages or payloads (may contain PII).
 */
export function logClientError(
  source: string,
  label: string,
  context?: Record<string, unknown>,
): void {
  const safeContext = context ?? {};
  console.error(`[${source}]`, label, safeContext);

  void import("@datadog/browser-rum")
    .then(({ datadogRum }) => {
      if (!datadogRum.getInitConfiguration()) return;
      datadogRum.addError(new Error(`[${source}] ${label}`), {
        source,
        ...safeContext,
      });
    })
    .catch(() => {
      // RUM unavailable (SSR, consent denied, or init pending).
    });
}

export function useRouteErrorLog(
  error: Error & { digest?: string },
  context?: RouteErrorContext,
): void {
  const boundary = context?.boundary;
  const app = context?.app;

  useEffect(() => {
    logRouteError(error, { boundary, app });
  }, [error, boundary, app]);
}
